import {
  Button,
  Form,
  FormInstance,
  InputNumber,
  Select,
  Space,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateUnpaidPolicyMutation,
  useGetUnpaidLeavePolicyQuery,
} from "../../../../features/attendanceSetting/attendanceSettingApi";
import { useGetLeaveTypeListQuery } from "../../../../features/leaveType/leaveTypeApi";
import { useGetPayrollSettingQuery } from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { selectArrayOption, selectSearchOption } from "../../../../utils/utils";
import { officeSettingsTabList } from "./officeSettingsTabList";

export default function Leave() {
  const [form] = Form.useForm();
  const { data, isError, error, isLoading } = useGetUnpaidLeavePolicyQuery();
  const [
    createUnpaidPolicy,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      error: createError,
      isSuccess: isCreateSuccess,
    },
  ] = useCreateUnpaidPolicyMutation();
  useError(isError, error, "Get unpaid data fail");
  useError(isCreateError, createError, "Update unpaid data fail");

  const isEnable = Form.useWatch("is_enable", form);

  const onFinish = (values: any) => {
    const newValues = {
      ...values,
      is_enable: values?.is_enable ? 1 : 0,
      // policy_type: "unpaid_leave",
      rules: values?.rules?.map((item: any) => ({
        ...item,
        ...(item?.action === "leave_adjustment" && {
          penalty_type: item?.leave_adjustment_type,
          penalty_amount: item?.leave_adjustment_amount,
          leave_adjustment_type: undefined,
          leave_adjustment_amount: undefined,
        }),
        ...(item?.action === "salary_adjustment" && {
          penalty_type: item?.salary_adjustment_type,
          penalty_amount: item?.salary_adjustment_amount,
          salary_adjustment_type: undefined,
          salary_adjustment_amount: undefined,
        }),
        ...(item?.action === "cash_penalty" && {
          penalty_type: null,
          penalty_amount: item?.cash_penalty_amount,
          cash_penalty_amount: undefined,
        }),
        ...(item?.action === "no_penalty" && {
          penalty_type: null,
          penalty_amount: 0,
        }),
      })),
      delete_rule_id: data?.data?.unpaid_leave_policy_rules
        ?.filter(
          (item) => !values?.rules?.some((el: any) => el?.id === item?.id)
        )
        ?.map((item) => item?.id),
    };

    createUnpaidPolicy(newValues);
  };

  useEffect(() => {
    if (data?.data) {
      form.setFieldsValue({
        is_enable: data?.data?.is_unpaid_leave_policy_enable ? true : false,
        rules: data?.data?.unpaid_leave_policy_rules?.map((item: any) => ({
          id: item?.id,
          from: item?.from_days,
          to: item?.to_days,
          action: item?.action,

          ...(item?.action === "leave_adjustment" && {
            leave_adjustment_type: item?.penalty_type,
            leave_adjustment_amount: item?.penalty_amount,
          }),
          ...(item?.action === "salary_adjustment" && {
            salary_adjustment_type: item?.penalty_type,
            salary_adjustment_amount: item?.penalty_amount,
          }),
          ...(item?.action === "cash_penalty" && {
            cash_penalty_amount: item?.penalty_amount,
          }),
        })),
      });
    }
  }, [data?.data, form]);

  useEffect(() => {
    if (isCreateSuccess) {
      toast("success", "Unpaid updated successfully");
    }
  }, [isCreateSuccess]);

  return (
    <SidebarLayout title="Office Settings" tabList={officeSettingsTabList}>
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
        initialValues={{
          is_enable: true,
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-header text-[22px] font-semibold">
            Unpaid Leave Policy
          </h3>
          {isEnable && (
            <label
              htmlFor="add-unpaid-leave"
              className="outline-button-primary border flex items-center py-1.5 px-2 cursor-pointer gap-1 rounded"
            >
              <AddIcon />
              <span>Add More</span>
            </label>
          )}
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="flex items-center gap-2 my-4">
              <Form.Item
                name="is_enable"
                valuePropName="checked"
                className="mb-0"
              >
                <Switch />
              </Form.Item>

              <div className="text-header text-sm font-medium">
                <label className="cursor-pointer select-none">
                  {isEnable ? "Enabled - " : "Disabled "}
                </label>
                {isEnable ? (
                  <span className="text-body-text font-normal">
                    {/* - After defined policy days, rest of the graced attendance
                    will be considered as late attendance */}
                    All leave requests exceeding available paid leave balance
                    will be treated as unpaid leave in accordance with the
                    policy.
                  </span>
                ) : null}
              </div>
            </div>
            <div className={!isEnable ? "hidden" : ""}>
              <Form.List initialValue={[undefined]} name="rules">
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <button
                        className="sr-only"
                        id="add-unpaid-leave"
                        onClick={() => add()}
                        type="button"
                      >
                        Add
                      </button>
                      {fields?.map((field, index) => (
                        <div
                          className={`flex items-center justify-between pt-2 pb-2 px-3 py-1 bg-[#fbfcfd] border border-light attendance-table ${
                            index === 0 ? "border-t" : "border-t-0"
                          }`}
                          key={field.key}
                        >
                          <div className="flex items-center gap-2.5">
                            <span className="text-display-body text-base font-medium">
                              For
                            </span>
                            <Form.Item
                              className="mb-0"
                              name={[index, "from"]}
                              rules={[
                                {
                                  required: true,
                                  message: "From is required",
                                },
                                {
                                  type: "number",
                                  min: 0,
                                  message: "From cannot be negative",
                                },
                              ]}
                            >
                              <InputNumber
                                type="number"
                                style={{ maxWidth: 60 }}
                                placeholder="-"
                                size="small"
                              />
                            </Form.Item>
                            <span className="text-display-body text-base font-medium">
                              to
                            </span>
                            <Form.Item
                              className="mb-0"
                              name={[index, "to"]}
                              rules={[
                                {
                                  required: true,
                                  message: "To is required",
                                },
                                {
                                  type: "number",
                                  min: 0,
                                  message: "To cannot be negative",
                                },
                              ]}
                            >
                              <InputNumber
                                type="number"
                                style={{ maxWidth: 60 }}
                                placeholder="-"
                                size="small"
                              />
                            </Form.Item>
                            <span className="text-display-body text-base font-medium">
                              Days
                            </span>
                            <Form.Item
                              className="mb-0"
                              name={[index, "action"]}
                              initialValue="no_penalty"
                              rules={[
                                {
                                  required: true,
                                  message: "Action is required",
                                },
                              ]}
                            >
                              <Select
                                showSearch
                                placeholder="Select"
                                options={grace_policy_rulesList}
                                style={{ width: 180 }}
                                suffixIcon={<SelectDownIcon />}
                                rootClassName="icon-gray"
                                size="small"
                              />
                            </Form.Item>

                            <SelectedItem form={form} index={index} />
                          </div>
                          <Button
                            className="action-button"
                            size="small"
                            icon={<DeleteIcon />}
                            onClick={() => {
                              remove(field.name);
                            }}
                          />
                        </div>
                      ))}
                    </>
                  );
                }}
              </Form.List>
            </div>
            <Space className="w-full pt-6 justify-end" size={12}>
              {/* <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button> */}

              {/* {isEnable && ( */}
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isCreateLoading}
              >
                Save
              </Button>
              {/* )} */}
            </Space>
          </>
        )}
      </Form>
    </SidebarLayout>
  );
}

type ListComponentType = {
  index: number;
  form: FormInstance<any>;
};

const SelectedItem = ({ index, form }: ListComponentType) => {
  const selectedItem = Form.useWatch(["rules", index, "action"], form);
  const {
    data: leaveData,
    isLoading: isLeaveLoading,
    isError: isLeaveError,
    error: leaveError,
  } = useGetLeaveTypeListQuery({
    params: {
      limit: 0,
      skip: 0,
    },
  });
  const { data, isLoading, isError, error } = useGetPayrollSettingQuery();
  useError(isLeaveError, leaveError, "Get lave data fail");
  useError(isError, error, "Get salary data fail");

  const [salaryList, setSalaryList] = useState([
    { title: "Gross Salary", id: "gross" },
  ]);

  const getSalaryData = data?.data?.payroll_setting?.salary_breakdown;

  const leaveTypes = leaveData?.data?.leave_types
    // ?.filter((el: any) => el?.is_published)
    ?.map((item: any) => ({
      label: item?.title,
      value: item?.id?.toString(),
    }));

  useEffect(() => {
    if (getSalaryData?.length) {
      setSalaryList((prev) => {
        const newItems = getSalaryData.filter(
          (item: any) => !prev.some((prevItem) => prevItem.id === item.id)
        );

        return [
          ...prev,
          ...newItems.map((item: any) => ({
            title: item?.title || "",
            id: item?.id?.toString(),
          })),
        ];
      });
    }
  }, [getSalaryData]);

  return selectedItem === "cash_penalty" ? (
    <>
      <span className="text-display-body text-base font-medium">
        Amount (BDT)
      </span>
      <Form.Item
        className="mb-0"
        name={[index, "cash_penalty_amount"]}
        rules={[
          {
            required: true,
            message: "Amount is required",
          },
          {
            type: "number",
            min: 0,
            message: "Amount cannot be negative",
          },
        ]}
      >
        <InputNumber
          type="number"
          style={{ width: 150 }}
          placeholder="-"
          size="small"
        />
      </Form.Item>
    </>
  ) : selectedItem === "leave_adjustment" ? (
    <>
      <Form.Item
        className="mb-0"
        name={[index, "leave_adjustment_amount"]}
        rules={[
          {
            required: true,
            message: "Days is required",
          },
          {
            type: "number",
            min: 0,
            message: "Days cannot be negative",
          },
        ]}
      >
        <InputNumber
          type="number"
          style={{ maxWidth: 60 }}
          placeholder="-"
          size="small"
        />
      </Form.Item>
      <span className="text-display-body text-base font-medium">Days of</span>
      <Form.Item
        className="mb-0"
        name={[index, "leave_adjustment_type"]}
        rules={[
          {
            required: true,
            message: "Type is required",
          },
        ]}
      >
        <Select
          // allowClear
          showSearch
          filterOption={selectSearchOption}
          placeholder="Select"
          options={leaveTypes}
          style={{ width: 180 }}
          suffixIcon={<SelectDownIcon />}
          rootClassName="icon-gray"
          size="small"
          loading={isLeaveLoading}
        />
      </Form.Item>
    </>
  ) : selectedItem === "salary_adjustment" ? (
    <>
      <Form.Item
        className="mb-0"
        name={[index, "salary_adjustment_amount"]}
        rules={[
          {
            required: true,
            message: "Days is required",
          },
          {
            type: "number",
            min: 0,
            message: "Days cannot be negative",
          },
        ]}
      >
        <InputNumber
          type="number"
          style={{ maxWidth: 60 }}
          placeholder="-"
          size="small"
        />
      </Form.Item>
      <span className="text-display-body text-base font-medium">Days of</span>
      <Form.Item
        className="mb-0"
        name={[index, "salary_adjustment_type"]}
        rules={[
          {
            required: true,
            message: "Type is required",
          },
        ]}
      >
        <Select
          // allowClear
          showSearch
          filterOption={selectSearchOption}
          placeholder="Select"
          options={selectArrayOption(salaryList || [], "title", "id")}
          style={{ width: 180 }}
          suffixIcon={<SelectDownIcon />}
          rootClassName="icon-gray"
          size="small"
          loading={isLoading}
        />
      </Form.Item>
    </>
  ) : null;
};

const grace_policy_rulesList = [
  { label: "No Penalty", value: "no_penalty" },
  // { label: "Leave Adjustment", value: "leave_adjustment" },
  { label: "Salary Adjustment", value: "salary_adjustment" },
  { label: "Cash Penalty", value: "cash_penalty" },
];
